import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { allowedChildren, groupHome, type Can, type NavGroup, type NavItem, type NavLinkComponent } from './types';

type Props = {
  groups: NavGroup[];
  pathname: string;
  can: Can;
  Link: NavLinkComponent;
  collapsed?: boolean;
  onNavigate?: () => void;
};

// the group a page belongs to: its own page, or any page under it
const within = (group: NavGroup, pathname: string) =>
  pathname === group.path || (group.children ?? []).some((item) => item.path === pathname);
// the pages under a group, in the order given, gathered by their heading (pages with no heading come first)
function headed(items: NavItem[]) {
  const out: { title?: string; items: NavItem[] }[] = [];
  for (const item of items) {
    const last = out[out.length - 1];
    if (last && last.title === item.group) last.items.push(item);
    else out.push({ title: item.group, items: [item] });
  }
  return out;
}

// the left menu: the top-level groups, each shown only when the admin may open something in it. The group
// whose page is open shows the pages under it; any other opens with its chevron. Shrunk to icons, only the
// groups show.
export function NavMenu({ groups, pathname, can, Link, collapsed = false, onNavigate }: Props) {
  // the group opened or closed by hand; the group of the page being looked at is open otherwise, and moving to
  // another page forgets the choice
  const [choice, setChoice] = useState<{ path: string; open: boolean } | null>(null);
  useEffect(() => setChoice(null), [pathname]);
  return (
    <nav className="admin-menu" aria-label="Admin sections">
      {groups.map((group) => {
        const home = groupHome(group, can);
        if (!home) return null;
        const children = allowedChildren(group, can);
        const active = within(group, pathname);
        const open = !collapsed && children.length > 0 && (choice?.path === group.path ? choice.open : active);
        const Icon = group.icon;
        return (
          <div className={`admin-menu-group${open ? ' is-open' : ''}`} key={group.path}>
            <div className="admin-menu-head">
              <Link
                to={home}
                end={group.path === '/'}
                title={collapsed ? group.label : undefined}
                onClick={onNavigate}
                className={() => `admin-menu-item${active ? ' is-active' : ''}`}
              >
                <Icon aria-hidden className="admin-menu-icon" />
                {!collapsed && <span>{group.label}</span>}
              </Link>
              {!collapsed && children.length > 0 && (
                <button
                  type="button"
                  className="admin-menu-toggle"
                  aria-label={`${open ? 'Hide' : 'Show'} ${group.label} pages`}
                  aria-expanded={open}
                  onClick={() => setChoice({ path: group.path, open: !open })}
                >
                  <ChevronDown aria-hidden />
                </button>
              )}
            </div>
            {open && (
              <div className="admin-menu-pages">
                {headed(children).map((part, index) => (
                  <div className="admin-menu-part" key={part.title ?? index}>
                    {part.title && <p className="admin-menu-title">{part.title}</p>}
                    {part.items.map(({ path, label, icon: PageIcon }) => (
                      <Link
                        key={path}
                        to={path}
                        onClick={onNavigate}
                        className={({ isActive }) => `admin-menu-item admin-menu-page${isActive ? ' is-active' : ''}`}
                      >
                        <PageIcon aria-hidden className="admin-menu-icon" />
                        <span>{label}</span>
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
